import {
  browserLocalPersistence,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
} from 'firebase/auth';
import { auth, googleProvider } from '../firebase';

const REDIRECT_PENDING_KEY = 'realtorch:google-redirect-pending';

// Start persistence at module load instead of inside the click handler.
// `await setPersistence(...)` before opening the window spends the user
// activation Safari requires for window.open, so the popup was blocked before
// it ever opened. getAuth() already defaults to local persistence, so this is
// belt-and-braces rather than load-bearing.
const persistenceReady = setPersistence(auth, browserLocalPersistence).catch(() => {});

// Popups that never opened, versus popups the user closed on purpose. Only the
// former should silently escalate to a redirect — retrying a dismissed popup as
// a full-page redirect would yank the page out from under someone who just
// changed their mind.
const POPUP_UNAVAILABLE = new Set([
  'auth/popup-blocked',
  'auth/operation-not-supported-in-this-environment',
  'auth/web-storage-unsupported',
]);

// Closing the Google window is a decision, not a failure. Now that the popup
// is the default path these codes are routine, so callers should return to the
// login form quietly rather than raising an error.
const POPUP_DISMISSED = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
]);

export function isSignInDismissed(error) {
  return POPUP_DISMISSED.has(error?.code);
}

function setRedirectPending() {
  try {
    window.sessionStorage.setItem(REDIRECT_PENDING_KEY, '1');
  } catch (error) {
    // Storage is disabled (private mode / locked-down webview). We lose the
    // diagnostic below, but sign-in itself is unaffected.
  }
}

/**
 * True when this tab kicked off a Google redirect that we have not yet
 * accounted for. Reading it clears it, so a caller can use it to tell "the
 * redirect came back empty" apart from "this is just a normal page load".
 */
export function consumeRedirectPending() {
  try {
    const pending = window.sessionStorage.getItem(REDIRECT_PENDING_KEY) === '1';
    window.sessionStorage.removeItem(REDIRECT_PENDING_KEY);
    return pending;
  } catch (error) {
    return false;
  }
}

/**
 * Sign in with Google, preferring the popup flow on every device.
 *
 * Mobile used to be pinned to signInWithRedirect on the theory that popups are
 * unreliable on phones. The redirect flow is the unreliable one: it round-trips
 * through the auth domain and reads the credential back out of a cross-origin
 * iframe, and every browser that partitions or blocks that storage (iOS Safari,
 * in-app webviews, private windows) drops it. getRedirectResult() then resolves
 * null with no error to show, and the app quietly re-renders /login — which is
 * exactly the "it authenticates and then does nothing" report. A popup hands
 * the credential straight back to the opener via postMessage and never touches
 * partitioned storage, so it survives all of those cases.
 */
export async function signInWithGoogle() {
  try {
    return await signInWithPopup(auth, googleProvider);
  } catch (error) {
    if (!POPUP_UNAVAILABLE.has(error?.code)) {
      throw error;
    }

    // No popup available at all — fall back to the redirect flow so sign-in is
    // still reachable, and leave a breadcrumb so a lost credential surfaces as
    // an error instead of a silent bounce back to the login screen.
    await persistenceReady;
    setRedirectPending();
    return signInWithRedirect(auth, googleProvider);
  }
}
